import { action, comparer, computed, runInAction, toJS, when } from 'mobx';

import { concatPath } from '@feathr/hooks';
import type { TConstraints, TRachisEmpty } from '@feathr/rachis';
import { Collection, isWretchError, wretch } from '@feathr/rachis';

import { IntegrationContactMappingStore } from './integration_contact_mappings';
import type { IIntegrationAttributes } from './integrations';
import { BaseIntegration } from './integrations';
import type {
  IIntegrationBaseMapping,
  TIntegrationDataType,
  TIntegrationPersonAttributes,
} from './integrations/integrations';
import { EIntegrationSyncRule } from './integrations/integrations';

export interface IVirtuousDownsyncActivities {
  contacts: boolean;
  gifts: boolean;
}

export interface IVirtuousUpsyncActivities {
  donations: boolean;
  /** Whether Feathr people create Virtuous contacts. */
  people: boolean;
  /** The Feathr forms whose submissions create contacts. Empty means every form. */
  form_ids: string[];
  /** Whether Feathr activity is written to Virtuous as Contact Notes. */
  activity: boolean;
}

export interface IVirtuousFundraising {
  /** The Virtuous project a Feathr donation is designated to. */
  project_id?: string | null;
}

export interface IVirtuousIntegrationAttributes extends IIntegrationAttributes {
  downsync_activities: IVirtuousDownsyncActivities;
  upsync_activities: IVirtuousUpsyncActivities;
  fundraising: IVirtuousFundraising;
  /** A Virtuous segment code stamped on every contact Feathr creates. */
  segment_code?: string | null;
  /** The Virtuous Contact Note type Feathr activity is written as. */
  activity_note_type?: string | null;
  /** The Feathr user who connected; `null` when that user is gone. */
  connected_by?: IVirtuousConnectedBy | null;
}

export interface IVirtuousConnectedBy {
  name: string | null;
  email: string | null;
}

/** Virtuous contact fields that can sync to Feathr. Values are magpie's Virtuous catalog ids. */
export enum EVirtuousContactMappings {
  addressCity = 'addressCity',
  addressCountry = 'addressCountry',
  addressLine1 = 'addressLine1',
  addressLine2 = 'addressLine2',
  addressPostal = 'addressPostal',
  addressState = 'addressState',
  approximateAge = 'approximateAge',
  birthDate = 'birthDate',
  contactType = 'contactType',
  email = 'email',
  firstName = 'firstName',
  gender = 'gender',
  id = 'id',
  individualId = 'individualId',
  informalName = 'informalName',
  isDeceased = 'isDeceased',
  lastGiftAmount = 'lastGiftAmount',
  lastGiftDate = 'lastGiftDate',
  lastName = 'lastName',
  lifeToDateGiving = 'lifeToDateGiving',
  middleName = 'middleName',
  name = 'name',
  phone = 'phone',
  prefix = 'prefix',
  suffix = 'suffix',
  tags = 'tags',
  yearToDateGiving = 'yearToDateGiving',
}

export type TVirtuousContactMappingKey = `${EVirtuousContactMappings}`;

/** The data type each Virtuous contact field syncs as. Mirrors magpie's catalog types. */
export const VIRTUOUS_CONTACT_DATA_TYPES: Readonly<
  Record<EVirtuousContactMappings, TIntegrationDataType>
> = {
  [EVirtuousContactMappings.addressCity]: 'str',
  [EVirtuousContactMappings.addressCountry]: 'str',
  [EVirtuousContactMappings.addressLine1]: 'str',
  [EVirtuousContactMappings.addressLine2]: 'str',
  [EVirtuousContactMappings.addressPostal]: 'str',
  [EVirtuousContactMappings.addressState]: 'str',
  [EVirtuousContactMappings.approximateAge]: 'int',
  [EVirtuousContactMappings.birthDate]: 'date',
  [EVirtuousContactMappings.contactType]: 'str',
  [EVirtuousContactMappings.email]: 'str',
  [EVirtuousContactMappings.firstName]: 'str',
  [EVirtuousContactMappings.gender]: 'str',
  [EVirtuousContactMappings.id]: 'str',
  [EVirtuousContactMappings.individualId]: 'str',
  [EVirtuousContactMappings.informalName]: 'str',
  [EVirtuousContactMappings.isDeceased]: 'bool',
  [EVirtuousContactMappings.lastGiftAmount]: 'float',
  [EVirtuousContactMappings.lastGiftDate]: 'date',
  [EVirtuousContactMappings.lastName]: 'str',
  [EVirtuousContactMappings.lifeToDateGiving]: 'float',
  [EVirtuousContactMappings.middleName]: 'str',
  [EVirtuousContactMappings.name]: 'str',
  [EVirtuousContactMappings.phone]: 'str',
  [EVirtuousContactMappings.prefix]: 'str',
  [EVirtuousContactMappings.suffix]: 'str',
  [EVirtuousContactMappings.tags]: 'list',
  [EVirtuousContactMappings.yearToDateGiving]: 'float',
};

/** Contact fields every connection maps. magpie seeds them, and the page can't change them. */
export const VIRTUOUS_REQUIRED_CONTACT_FIELDS: ReadonlySet<string> =
  new Set<TVirtuousContactMappingKey>([
    EVirtuousContactMappings.id,
    EVirtuousContactMappings.email,
  ]);

export type TVirtuousPairDirection = 'downsync' | 'upsync' | 'both';

/** One row of magpie's mapping-pair table, as anhinga passes it through. */
export interface IVirtuousMappingPair extends Record<string, unknown> {
  id?: string;
  pairing?: string;
  /** The Feathr person field, e.g. `first_name`, `address_locality` or `custom_fields.<u_key>`. */
  hub_field?: string | null;
  /** The Virtuous field, a catalog id. */
  integration_field?: string | null;
  /** A fixed value; a literal pair names only one field. */
  literal?: string | null;
  direction?: TVirtuousPairDirection;
  enabled?: boolean;
  status?: 'syncing' | 'stopped';
  stopped_reason?: string | null;
}

export interface IVirtuousMappingPairTable extends Record<string, unknown> {
  pairs?: IVirtuousMappingPair[];
}

/** The pairing that holds a contact's fields. */
export const VIRTUOUS_CONTACT_PAIRING = 'contact_person';

/** A contact field mapping, as the mapping table reads one pair. */
export interface IVirtuousContactMapping extends IIntegrationBaseMapping {
  key: TVirtuousContactMappingKey;
  integration: IVirtuousIntegrationAttributes['id'];
  default_field: TIntegrationPersonAttributes | string | null;
  type: 'VirtuousContactMapping';
  status?: 'syncing' | 'stopped';
  stopped_reason?: string | null;
}

/** A change to one contact mapping. Set one of the two fields and clear the other with `null`. */
export interface IVirtuousContactMappingUpdate {
  customField: string | null;
  defaultField: TIntegrationPersonAttributes | null;
  mapping: IVirtuousContactMapping['id'];
}

export type TVirtuousSyncSummaryField = 'last_synced' | 'mappings_need_attention';

export interface IVirtuousSyncSummary extends Record<string, unknown> {
  /** ISO 8601 time of the latest completed sync, `null` if it never synced. */
  last_synced: string | null;
  /** Mapping pairs that stopped syncing. */
  mappings_need_attention: number;
  /** `null` before a connection exists. */
  is_healthy: boolean | null;
}

export interface IVirtuousOption {
  id: string;
  name: string;
}

/** The attributes the page stages for Save & Sync. anhinga refuses any other top-level key. */
export const VIRTUOUS_STAGED_ATTRIBUTES = [
  'activity_note_type',
  'downsync_activities',
  'fundraising',
  'upsync_activities',
] as const satisfies ReadonlyArray<keyof IVirtuousIntegrationAttributes>;

export type TVirtuousStagedAttribute = (typeof VIRTUOUS_STAGED_ATTRIBUTES)[number];

/** `meta.code` anhinga sends when the key belongs to a connection that is already healthy. */
export const VIRTUOUS_ALREADY_CONNECTED_CODE = 'already_connected';

const CUSTOM_FIELDS_ROOT = 'custom_fields.';
const ADDRESS_HUB_PREFIX = 'address_';
const ADDRESS_FEATHR_PREFIX = 'address.';
const PENDING_PREFIX = 'pending:';

/** A hub field as the Feathr field the mapping table shows: `address_locality` → `address.locality`. */
export function hubFieldToFeathrField(hubField: string): string {
  return hubField.startsWith(ADDRESS_HUB_PREFIX)
    ? `${ADDRESS_FEATHR_PREFIX}${hubField.slice(ADDRESS_HUB_PREFIX.length)}`
    : hubField;
}

/** A Feathr field as magpie's hub field: `address.locality` → `address_locality`. */
export function feathrFieldToHubField(feathrField: string): string {
  return feathrField.startsWith(ADDRESS_FEATHR_PREFIX)
    ? `${ADDRESS_HUB_PREFIX}${feathrField.slice(ADDRESS_FEATHR_PREFIX.length)}`
    : feathrField;
}

/** The hub field that names a Feathr custom field. The mapping table uses it as the field's id. */
export function customFieldHubField(uKey: string): string {
  return `${CUSTOM_FIELDS_ROOT}${uKey}`;
}

/** Whether a pair is a contact field the customer maps from Virtuous to Feathr. */
function isContactFieldPair(pair: IVirtuousMappingPair): boolean {
  return (
    pair.pairing === VIRTUOUS_CONTACT_PAIRING &&
    pair.direction === 'downsync' &&
    !!pair.id &&
    !!pair.hub_field &&
    !!pair.integration_field
  );
}

/** A contact field pair as a contact mapping. The mapping's key is the Virtuous field. */
export function pairToContactMapping(
  pair: IVirtuousMappingPair,
  integrationId: string,
): IVirtuousContactMapping {
  const hubField = pair.hub_field ?? '';
  const isCustom = hubField.startsWith(CUSTOM_FIELDS_ROOT);
  const key = (pair.integration_field ?? '') as TVirtuousContactMappingKey;
  const isRequired = VIRTUOUS_REQUIRED_CONTACT_FIELDS.has(key);
  return {
    custom_field: isCustom ? hubField : null,
    data_type: VIRTUOUS_CONTACT_DATA_TYPES[key as EVirtuousContactMappings] ?? 'str',
    date_last_synced: null,
    default_field: isCustom ? null : hubFieldToFeathrField(hubField),
    editable: !isRequired,
    id: pair.id ?? '',
    integration: integrationId,
    key,
    required: isRequired,
    status: pair.status,
    stopped_reason: pair.stopped_reason,
    sync_rule: EIntegrationSyncRule.downSync,
    type: 'VirtuousContactMapping',
  };
}

/** The contact mappings in a pair table: only the contact fields that sync to Feathr. */
export function pairTableToContactMappings(
  table: IVirtuousMappingPairTable,
  integrationId: string,
): IVirtuousContactMapping[] {
  return (table.pairs ?? [])
    .filter(isContactFieldPair)
    .map((pair) => pairToContactMapping(pair, integrationId));
}

export class VirtuousContactMappingStore extends IntegrationContactMappingStore<
  IVirtuousContactMapping,
  TVirtuousContactMappingKey,
  IVirtuousContactMappingUpdate
> {}

export class VirtuousIntegration extends BaseIntegration<IVirtuousIntegrationAttributes> {
  public readonly className = 'VirtuousIntegration';

  public get constraints(): TConstraints<IVirtuousIntegrationAttributes> {
    return {};
  }

  public getItemUrl(pathSuffix: string | undefined): string {
    return concatPath(`integrations/virtuous/${this.id}`, pathSuffix);
  }

  public get name(): string {
    return 'virtuous_integration';
  }

  private contactMappingStoreInstance?: VirtuousContactMappingStore;

  /** Observable contact mappings, shared by every reader of this integration. */
  public get contactMappingStore(): VirtuousContactMappingStore {
    this.contactMappingStoreInstance ??= new VirtuousContactMappingStore(this);
    return this.contactMappingStoreInstance;
  }

  /** The staged attributes whose value differs from the saved one. */
  @computed
  public get changedStagedAttributes(): TVirtuousStagedAttribute[] {
    return VIRTUOUS_STAGED_ATTRIBUTES.filter(
      (attribute) =>
        this.isAttributeDirty(attribute) &&
        !comparer.structural(toJS(this.get(attribute)), toJS(this.shadowAttributes[attribute])),
    );
  }

  /** The changed staged attributes as one PATCH body. */
  public getStagedPatch(): Partial<IVirtuousIntegrationAttributes> {
    return Object.fromEntries(
      this.changedStagedAttributes.map((attribute) => [attribute, toJS(this.get(attribute))]),
    );
  }

  /** Puts every dirty staged attribute back to its saved value and marks it clean. */
  @action
  public discardStagedChanges(): void {
    VIRTUOUS_STAGED_ATTRIBUTES.filter((attribute) => this.isAttributeDirty(attribute)).forEach(
      (attribute) => {
        this.set({ [attribute]: this.shadowAttributes[attribute] }, false);
        this.setAttributeClean(attribute);
      },
    );
  }

  private url(pathSuffix: string): string {
    this.assertCollection(this.collection);
    return `${this.collection.url()}${this.id}/${pathSuffix}`;
  }

  /** Sends one request to the integration's API and returns its data. */
  private async request<T extends Record<string, unknown> | Array<Record<string, unknown>> | null>(
    url: string,
    method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
    body?: object,
  ): Promise<T> {
    this.assertCollection(this.collection);
    const response = await wretch<T>(url, {
      headers: this.collection.getHeaders(),
      method,
      body: body ? JSON.stringify(body) : undefined,
    });
    if (isWretchError(response)) {
      throw response.error;
    }
    return response.data;
  }

  /** Links the integration with the customer's Virtuous API key and applies the connected record. */
  @action
  public async connect(apiKey: string): Promise<void> {
    const attributes = await this.request<IVirtuousIntegrationAttributes>(
      this.url('connect'),
      'POST',
      { api_key: apiKey },
    );
    // The server saved these values, so the page must not offer them as unsaved changes.
    this.applyServerAttributes(attributes);
  }

  public getMappingPairs(): Promise<IVirtuousMappingPairTable> {
    return this.request<IVirtuousMappingPairTable>(this.url('mapping_pairs'), 'GET');
  }

  public async getContactMappings(): Promise<IVirtuousContactMapping[]> {
    return pairTableToContactMappings(await this.getMappingPairs(), this.id);
  }

  /**
   * magpie creates a pair only with both fields, so a new mapping waits on the client until it
   * has a Feathr field; `updateContactMapping` then creates the pair.
   */
  public async addContactMappings(
    keys: TVirtuousContactMappingKey[],
  ): Promise<IVirtuousContactMapping[]> {
    return keys.map((key) =>
      pairToContactMapping(
        {
          direction: 'downsync',
          hub_field: '',
          id: `${PENDING_PREFIX}${key}`,
          integration_field: key,
          pairing: VIRTUOUS_CONTACT_PAIRING,
        },
        this.id,
      ),
    );
  }

  /**
   * Points a mapping at a Feathr field. A pending mapping creates its pair; a mapping set to no
   * field is deleted, because magpie can't clear one side of a pair. Resolves `null` on a delete.
   */
  @action
  public async updateContactMapping({
    customField,
    defaultField,
    mapping,
  }: IVirtuousContactMappingUpdate): Promise<IVirtuousContactMapping | null> {
    const feathrField = customField ?? defaultField;
    const isPending = mapping.startsWith(PENDING_PREFIX);
    if (!feathrField) {
      if (!isPending) {
        await this.deleteContactMapping(mapping);
      }
      return null;
    }
    const hubField = feathrFieldToHubField(feathrField);
    const table = isPending
      ? await this.request<IVirtuousMappingPairTable>(this.url('mapping_pairs'), 'POST', {
          direction: 'downsync',
          hub_field: hubField,
          integration_field: mapping.slice(PENDING_PREFIX.length),
          pairing: VIRTUOUS_CONTACT_PAIRING,
        })
      : await this.request<IVirtuousMappingPairTable>(
          this.url(`mapping_pairs/${mapping}`),
          'PATCH',
          { hub_field: hubField },
        );
    const integrationField = isPending ? mapping.slice(PENDING_PREFIX.length) : undefined;
    const saved = pairTableToContactMappings(table, this.id).find(({ id, key }) =>
      integrationField ? key === integrationField : id === mapping,
    );
    return saved ?? null;
  }

  @action
  public async deleteContactMapping(id: string): Promise<void> {
    if (id.startsWith(PENDING_PREFIX)) {
      return;
    }
    this.assertCollection(this.collection);
    const response = await wretch<TRachisEmpty>(this.url(`mapping_pairs/${id}`), {
      headers: this.collection.getHeaders(),
      method: 'DELETE',
    });
    if (isWretchError(response)) {
      throw response.error;
    }
  }

  public getSyncSummary(): Promise<IVirtuousSyncSummary> {
    return this.request<IVirtuousSyncSummary>(this.url('sync_summary'), 'GET');
  }

  /** The Virtuous projects a donation can be designated to. */
  public async getProjects(): Promise<IVirtuousOption[]> {
    const response = await this.request<{ projects: IVirtuousOption[] }>(
      this.url('projects'),
      'GET',
    );
    return response.projects ?? [];
  }

  /** The Virtuous Contact Note types Feathr activity can be written as. */
  public async getNoteTypes(): Promise<IVirtuousOption[]> {
    const response = await this.request<{ note_types: IVirtuousOption[] }>(
      this.url('note_types'),
      'GET',
    );
    return response.note_types ?? [];
  }
}

export class VirtuousIntegrations extends Collection<VirtuousIntegration> {
  public getModel(attributes: IVirtuousIntegrationAttributes): VirtuousIntegration {
    return new VirtuousIntegration(attributes);
  }

  public getClassName(): string {
    return 'virtuous_integration';
  }

  public override url(): string {
    return `${this.getHostname()}integrations/virtuous/`;
  }

  /**
   * Returns the account's integration, and creates it first when there is none (an account has at
   * most one). Rejects when the list fails, so a failed load never creates a second record.
   */
  public async findOrCreate(): Promise<VirtuousIntegration> {
    let response = this.list();
    if (response.isErrored) {
      response = this.list({}, { reset: true });
    }
    await when(() => !response.isPending);
    if (response.isErrored) {
      throw response.error ?? new Error('The integration list did not load.');
    }
    if (response.models.length) {
      return response.models[0];
    }
    // Skip the list refresh: it empties the list while it reloads, which unmounts the page.
    const created = await this.add(this.create(), { refreshApiCache: false, validate: false });
    if (created.isErrored) {
      throw created.error ?? new Error('The integration was not created.');
    }
    runInAction(() => response.models.push(created));
    return created;
  }
}
